
export interface Rule { find?: string; replace?: string; pattern?: string; label?: string; }
export interface Agent { id: string; name: string; systemPrompt: string; rules: Rule[]; outputFormat: string; created: string; }

export const PREBUILT_AGENTS: Agent[] = [
  {
    id: 'email_refiner', name: 'Email Refiner', outputFormat: 'text',
    systemPrompt: 'You are a professional communication coach. Make emails concise, clear, and action-oriented. Use active voice. Remove filler words. Add clear subject lines and call-to-action.',
    rules: [
      { find: 'very|really|quite|rather|fairly', replace: '' },
      { find: 'I just wanted to|I was wondering if|I thought I would', replace: '' },
      { find: "please let me know|feel free to|don't hesitate to", replace: "[ACTION REQUIRED]:" },
      { find: 'thanks in advance', replace: 'Thank you,' },
    ],
    created: 'built-in'
  },
  {
    id: 'meeting_summarizer', name: 'Meeting Summarizer', outputFormat: 'structured',
    systemPrompt: 'Extract decisions, action items, and open questions from meeting notes. Format as structured summary.',
    rules: [
      { pattern: '(decided|agreed|resolved)', label: 'DECISION' },
      { pattern: '(action item|todo|task)', label: 'ACTION' },
      { pattern: '(question|clarify|unclear)', label: 'QUESTION' },
    ],
    created: 'built-in'
  },
  {
    id: 'code_explainer', name: 'Code Explainer', outputFormat: 'structured',
    systemPrompt: 'Explain code in plain English. Identify the purpose, inputs, outputs, and key logic. Use analogies where helpful. Flag potential issues.',
    rules: [
      { pattern: '(function|const|let|var|class|interface)', label: 'DEFINITION' },
      { pattern: '(if|for|while|switch|try|catch)', label: 'CONTROL_FLOW' },
      { pattern: '(return|throw|break|continue)', label: 'EXIT_POINT' },
    ],
    created: 'built-in'
  },
  {
    id: 'translator', name: 'Biz-Tech Translator', outputFormat: 'text',
    systemPrompt: 'Translate between technical jargon and business language. Preserve meaning while adapting vocabulary for the audience.',
    rules: [
      { find: 'API', replace: 'system connection' },
      { find: 'database', replace: 'data storage' },
      { find: 'frontend', replace: 'user interface' },
      { find: 'backend', replace: 'server system' },
      { find: 'deploy', replace: 'go live' },
      { find: 'CI/CD', replace: 'automated quality checks' },
    ],
    created: 'built-in'
  },
  {
    id: 'rfp_helper', name: 'RFP Helper', outputFormat: 'structured',
    systemPrompt: 'Analyze RFP requirements and suggest response structure. Identify compliance gaps and win themes.',
    rules: [
      { pattern: '(must|shall|required|mandatory)', label: 'REQUIREMENT' },
      { pattern: '(evaluation|scoring|criteria|weight)', label: 'EVALUATION' },
      { pattern: '(deadline|due date|submission)', label: 'TIMELINE' },
    ],
    created: 'built-in'
  }
];

export function executeRuleAgent(agent: Agent, input: string): { output: string; mode: string; highlights: { text: string; label: string }[] } {
  let output = input;
  const highlights: { text: string; label: string }[] = [];

  agent.rules.forEach(rule => {
    if (rule.find) {
      const regex = new RegExp(rule.find, 'gi');
      output = output.replace(regex, rule.replace || '');
    }
    if (rule.pattern && rule.label) {
      const regex = new RegExp(rule.pattern, 'gi');
      const matches = input.match(regex);
      if (matches) matches.forEach(m => highlights.push({ text: m, label: rule.label! }));
    }
  });

  if (agent.outputFormat === 'structured' && highlights.length > 0) {
    output = highlights.reduce((acc, h) => {
      if (!acc.includes(`【${h.label}】`)) acc += `\n\n【${h.label}s】\n`;
      acc += `- ${h.text}\n`;
      return acc;
    }, `【Processed Output】\n\n${output}\n\n---\n【Highlights】`);
  }

  return { output, mode: 'rule-based', highlights };
}
